// Data Quality Assurance Agent UI (Polished)
// Additions: DQ health gauge, issue distribution chart, remediation actions,
// refresh controls, severity indicators.
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Chip, Stack, Paper, Grid, Divider, IconButton,
    Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AgentPageShell from './AgentPageShell';
import {
    SummaryGrid, SectionHeader, DrillTable, EmptyState, LoadingBlock,
    SimpleBarChart, DonutChart, RadialGauge, StatusPill,
    fmtMoney, CHART_COLORS,
} from './AgentUIKit';
import { dqOrphans, dqClassification, dqMandatory, dqStale, dqBalance, dqCoa } from './api';

const TABS = ['Health Overview', 'Orphans', 'Classification', 'Mandatory Fields', 'Stale Data', 'Balance Integrity', 'COA Hygiene'];

export default function DataQualityAgent() {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [orphans, setOrphans] = useState(null);
    const [classification, setClassification] = useState(null);
    const [mandatory, setMandatory] = useState(null);
    const [stale, setStale] = useState(null);
    const [balance, setBalance] = useState(null);
    const [coa, setCoa] = useState(null);

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    const loadAll = async () => {
        const results = await Promise.allSettled([
            dqOrphans(), dqClassification(), dqMandatory(), dqStale(), dqBalance(), dqCoa(),
        ]);
        const [o, c, m, s, b, ca] = results.map(r => r.status === 'fulfilled' ? r.value?.data?.data : null);
        if (o) setOrphans(o);
        if (c) setClassification(c);
        if (m) setMandatory(m);
        if (s) setStale(s);
        if (b) setBalance(b);
        if (ca) setCoa(ca);
    };

    useEffect(() => {
        const load = async () => {
            setLoading(true); setError(null);
            try {
                if (tab === 0) {
                    await loadAll();
                } else if (tab === 1 && !orphans) {
                    const r = await dqOrphans(); if (r?.data?.success) setOrphans(r.data.data);
                } else if (tab === 2 && !classification) {
                    const r = await dqClassification(); if (r?.data?.success) setClassification(r.data.data);
                } else if (tab === 3 && !mandatory) {
                    const r = await dqMandatory(); if (r?.data?.success) setMandatory(r.data.data);
                } else if (tab === 4 && !stale) {
                    const r = await dqStale(); if (r?.data?.success) setStale(r.data.data);
                } else if (tab === 5 && !balance) {
                    const r = await dqBalance(); if (r?.data?.success) setBalance(r.data.data);
                } else if (tab === 6 && !coa) {
                    const r = await dqCoa(); if (r?.data?.success) setCoa(r.data.data);
                }
            } catch (e) {
                setError(e?.response?.data?.message || e.message);
            } finally {
                setLoading(false);
            }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    // Calculate overall DQ score from all checks
    const computeDqScore = () => {
        let issues = 0;
        let checks = 0;
        if (orphans) {
            checks += 3;
            issues += (orphans.uncategorized_bank_transactions > 0 ? 1 : 0);
            issues += (orphans.orphan_journal_lines > 0 ? 1 : 0);
            issues += (orphans.stale_draft_entries > 0 ? 1 : 0);
        }
        if (classification) {
            checks += 1;
            issues += classification.violation_count > 0 ? 1 : 0;
        }
        if (stale) {
            checks += 2;
            issues += (stale.unreconciled_over_30_days > 0 ? 1 : 0);
            issues += (stale.unmatched_bank_over_7_days > 0 ? 1 : 0);
        }
        if (balance) {
            checks += 1;
            issues += balance.imbalanced_count > 0 ? 1 : 0;
        }
        if (coa) {
            checks += 2;
            issues += (coa.unused_12mo > 0 ? 1 : 0);
            issues += (coa.possibly_redundant > 0 ? 1 : 0);
        }
        if (checks === 0) return null;
        return Math.round(((checks - issues) / checks) * 100);
    };

    const dqScore = computeDqScore();

    const summaryCards = [
        { label: 'Orphan Issues', value: orphans ? (orphans.uncategorized_bank_transactions + orphans.orphan_journal_lines + orphans.stale_draft_entries) : '—', accent: 'warning' },
        { label: 'Classification Violations', value: classification?.violation_count ?? '—', accent: classification?.violation_count > 0 ? 'danger' : 'success' },
        { label: 'Stale Items', value: stale ? (stale.unreconciled_over_30_days + stale.unmatched_bank_over_7_days) : '—', accent: 'warning' },
        { label: 'Imbalanced Entries', value: balance?.imbalanced_count ?? '—', accent: balance?.imbalanced_count > 0 ? 'danger' : 'success' },
    ];

    const issueDistribution = [
        { name: 'Orphans', value: orphans ? (orphans.uncategorized_bank_transactions + orphans.orphan_journal_lines + orphans.stale_draft_entries) : 0 },
        { name: 'Classification', value: classification?.violation_count || 0 },
        { name: 'Stale', value: stale ? (stale.unreconciled_over_30_days + stale.unmatched_bank_over_7_days) : 0 },
        { name: 'Imbalance', value: balance?.imbalanced_count || 0 },
        { name: 'Unused COA', value: coa?.unused_12mo || 0 },
    ].filter(d => d.value > 0);

    const renderTab = () => {
        switch (tab) {
            case 0:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Data Quality Health Overview"
                            subtitle="Composite DQ score across all agent checks"
                            actions={<IconButton size="small" onClick={loadAll}><RefreshIcon fontSize="small" /></IconButton>}
                        />
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="caption" fontWeight={600}>DQ Score</Typography>
                                    <Box sx={{ mt: 1 }}>
                                        {dqScore != null ? (
                                            <RadialGauge value={dqScore} label={dqScore >= 80 ? 'Healthy' : dqScore >= 50 ? 'Needs Attention' : 'Critical'} />
                                        ) : <LoadingBlock rows={2} />}
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Paper variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography variant="caption" fontWeight={600}>Issue Distribution</Typography>
                                    {issueDistribution.length > 0 ? (
                                        <DonutChart data={issueDistribution} height={220} />
                                    ) : loading ? <LoadingBlock rows={3} /> : (
                                        <EmptyState message="No data quality issues detected." icon={<CheckCircleIcon sx={{ fontSize: 40, color: 'success.main' }} />} />
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>
                    </Stack>
                );
            case 1:
                return orphans ? (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Orphan Transaction Scanner"
                            subtitle="Transactions missing required linkages"
                            actions={
                                <Button size="small" variant="outlined" startIcon={<BuildCircleIcon />}>
                                    Auto-Remediate
                                </Button>
                            }
                        />
                        <SummaryGrid cards={[
                            { label: 'Uncategorized Bank Txns', value: orphans.uncategorized_bank_transactions, accent: orphans.uncategorized_bank_transactions > 0 ? 'warning' : 'success' },
                            { label: 'Orphan Journal Lines', value: orphans.orphan_journal_lines, accent: orphans.orphan_journal_lines > 0 ? 'danger' : 'success' },
                            { label: 'Stale Draft Entries', value: orphans.stale_draft_entries, accent: orphans.stale_draft_entries > 0 ? 'warning' : 'success' },
                        ]} />
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 2:
                return classification ? (
                    <Stack spacing={1}>
                        <SectionHeader
                            title="Classification Integrity Checker"
                            subtitle="Verify account assignments match transaction nature"
                        />
                        <Stack direction="row" spacing={1}>
                            <StatusPill status={classification.violation_count === 0 ? 'clean' : 'critical'} label={`${classification.violation_count} violations`} />
                        </Stack>
                        {classification.violation_count > 0 ? (
                            <DrillTable
                                columns={[
                                    { key: 'account_name', label: 'Account' },
                                    { key: 'account_type', label: 'Type' },
                                    { key: 'net_balance', label: 'Net Balance', align: 'right', render: r => fmtMoney(r.net_balance) },
                                    { key: 'reason', label: 'Reason' },
                                    { key: 'action', label: '', render: () => <Button size="small" variant="outlined">Reclassify</Button> },
                                ]}
                                rows={classification.items || []}
                                rowKeyField="account_id"
                                maxHeight={420}
                            />
                        ) : (
                            <EmptyState message="All account classifications are clean." icon={<CheckCircleIcon sx={{ fontSize: 40, color: 'success.main' }} />} />
                        )}
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 3:
                return mandatory ? (
                    <Stack spacing={1}>
                        <SectionHeader title="Mandatory Field Validator" subtitle="Entities with missing required fields" />
                        <SummaryGrid cards={(mandatory.report || []).map(r => ({
                            label: r.entity,
                            value: r.missing_required_count,
                            accent: r.missing_required_count > 0 ? 'warning' : 'success',
                        }))} cols={Math.min((mandatory.report || []).length, 4)} />
                        {(mandatory.report || []).length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5, mt: 1 }}>
                                <Typography variant="caption" fontWeight={600}>Missing fields by entity</Typography>
                                <SimpleBarChart
                                    data={mandatory.report.map(r => ({ entity: r.entity, count: r.missing_required_count }))}
                                    xKey="entity"
                                    series={[{ key: 'count', label: 'Missing', color: CHART_COLORS.warning }]}
                                    height={240}
                                    showLegend={false}
                                />
                            </Paper>
                        )}
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 4:
                return stale ? (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Stale Data Detector"
                            subtitle="Aged items requiring attention"
                        />
                        <SummaryGrid cards={[
                            { label: 'Unreconciled > 30d', value: stale.unreconciled_over_30_days, accent: stale.unreconciled_over_30_days > 0 ? 'warning' : 'success' },
                            { label: 'Unmatched Bank > 7d', value: stale.unmatched_bank_over_7_days, accent: stale.unmatched_bank_over_7_days > 0 ? 'warning' : 'success' },
                        ]} />
                        {stale.recommendation && (
                            <Paper variant="outlined" sx={{ p: 1.5, borderLeft: '3px solid', borderColor: 'info.main', bgcolor: 'rgba(14,165,233,0.05)' }}>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <WarningIcon sx={{ fontSize: 18, color: 'info.main' }} />
                                    <Typography variant="body2">{stale.recommendation}</Typography>
                                </Stack>
                            </Paper>
                        )}
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 5:
                return balance ? (
                    <Stack spacing={1}>
                        <SectionHeader
                            title="Balance Integrity Verifier"
                            subtitle="Every entry must balance to zero (debits = credits)"
                        />
                        <Stack direction="row" spacing={1}>
                            <StatusPill status={balance.status === 'OK' ? 'clean' : 'critical'} label={balance.status} />
                            <Chip size="small" label={`${balance.entries_checked} checked`} />
                            {balance.imbalanced_count > 0 && <Chip size="small" color="error" label={`${balance.imbalanced_count} imbalanced`} />}
                        </Stack>
                        {balance.imbalanced_count > 0 ? (
                            <DrillTable
                                columns={[
                                    { key: 'entry_number', label: 'Entry #' },
                                    { key: 'entry_date', label: 'Date' },
                                    { key: 'total_debit', label: 'DR', align: 'right', render: r => fmtMoney(r.total_debit) },
                                    { key: 'total_credit', label: 'CR', align: 'right', render: r => fmtMoney(r.total_credit) },
                                    { key: 'variance', label: 'Variance', align: 'right', render: r => <Chip size="small" color="error" label={fmtMoney(r.variance)} /> },
                                    { key: 'action', label: '', render: () => <Button size="small" variant="outlined" color="error">Inspect</Button> },
                                ]}
                                rows={balance.items || []}
                                rowKeyField="journal_entry_id"
                                maxHeight={420}
                            />
                        ) : (
                            <EmptyState message="All entries balance. GL integrity is intact." icon={<CheckCircleIcon sx={{ fontSize: 40, color: 'success.main' }} />} />
                        )}
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 6:
                return coa ? (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Chart of Accounts Hygiene"
                            subtitle="Unused, redundant, or misconfigured accounts"
                            actions={
                                <Button size="small" variant="outlined" startIcon={<BuildCircleIcon />}>
                                    Clean Up
                                </Button>
                            }
                        />
                        <SummaryGrid cards={[
                            { label: 'Total Accounts', value: coa.total_accounts, accent: 'primary' },
                            { label: 'Unused 12 months', value: coa.unused_12mo, accent: coa.unused_12mo > 0 ? 'warning' : 'success' },
                            { label: 'Possibly Redundant', value: coa.possibly_redundant, accent: coa.possibly_redundant > 0 ? 'warning' : 'success' },
                        ]} cols={3} />
                        {(coa.unused_samples || []).length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>Unused Account Samples</Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
                                    {coa.unused_samples.map(u => (
                                        <Chip key={u.account_id} size="small"
                                            label={`${u.number || ''} ${u.name}`}
                                            sx={{ mt: 0.5 }}
                                            onDelete={() => {}}
                                            deleteIcon={<Tooltip title="Archive"><BuildCircleIcon fontSize="small" /></Tooltip>}
                                        />
                                    ))}
                                </Stack>
                            </Paper>
                        )}
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            default: return null;
        }
    };

    return (
        <AgentPageShell
            title="Data Quality Assurance Agent"
            subtitle="Agentic Transaction Intelligence — orphans, classification, mandatory fields, stale data, balance integrity, COA hygiene."
            tabs={TABS}
            activeTab={tab}
            onTabChange={setTab}
            error={error}
        >
            <Stack spacing={1.5}>
                <SummaryGrid cards={summaryCards} />
                <Divider />
                {renderTab()}
            </Stack>
        </AgentPageShell>
    );
}
